import { useEffect, useState } from 'react';
import type { Coordinates } from '@/shared/types';
import { storage } from '@/shared/utils/storage';

export type GeoStatus = 'idle' | 'prompting' | 'granted' | 'denied' | 'unavailable';

interface GeolocationState {
  coords: Coordinates | null;
  status: GeoStatus;
  /** Ответа можно больше не ждать: иначе экраны ходят за данными дважды */
  isSettled: boolean;
}

/** Разрешение выдано: ответ придёт, ждём столько же, сколько браузер */
const GRANTED_SETTLE_MS = 10_000;

/** Разрешения нет: ответа может не быть вовсе */
const DEFAULT_SETTLE_MS = 700;

const CACHE_KEY = 'geo:last';

// Старше — не верим: человек мог уехать
const CACHE_TTL_MS = 60 * 60 * 1000;

// Сдвиг меньше — отдаём прежний объект, чтобы экраны не перезапрашивали данные
const MOVE_THRESHOLD_M = 200;

function readCache(): Coordinates | null {
  try {
    const cached = JSON.parse(storage.get(CACHE_KEY) ?? 'null');
    return cached && Date.now() - cached.at < CACHE_TTL_MS ? cached.coords : null;
  } catch {
    return null;
  }
}

let lastCoords: Coordinates | null = readCache();
let pending: Promise<Coordinates> | null = null;

function distanceM(a: Coordinates, b: Coordinates): number {
  const toRad = Math.PI / 180;
  const x =
    (b.longitude - a.longitude) *
    toRad *
    Math.cos(((a.latitude + b.latitude) / 2) * toRad);
  const y = (b.latitude - a.latitude) * toRad;
  return Math.hypot(x, y) * 6_371_000;
}

function remember(coords: Coordinates): Coordinates {
  if (!lastCoords || distanceM(lastCoords, coords) >= MOVE_THRESHOLD_M) {
    lastCoords = coords;
  }
  storage.set(CACHE_KEY, JSON.stringify({ coords: lastCoords, at: Date.now() }));
  return lastCoords;
}

/** Один запрос к браузеру на всех, кто спросил одновременно */
export function requestPosition(): Promise<Coordinates> {
  pending ??= new Promise<Coordinates>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve(
          remember({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          })
        ),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          lastCoords = null;
          storage.remove(CACHE_KEY);
        }
        reject(err);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  }).finally(() => {
    pending = null;
  });

  return pending;
}

async function isPermissionGranted(): Promise<boolean> {
  if (!navigator.permissions?.query) return false;

  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    // Safari до 16 не знает geolocation в permissions
    return false;
  }
}

/**
 * Геопозиция пользователя. Известную точку отдаёт сразу и уточняет в фоне;
 * при отказе — coords = null и статус.
 */
export function useGeolocation(enabled = true): GeolocationState {
  const [state, setState] = useState<GeolocationState>(() =>
    enabled && lastCoords
      ? { coords: lastCoords, status: 'granted', isSettled: true }
      : { coords: null, status: 'idle', isSettled: false }
  );

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, isSettled: true }));
      return;
    }

    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setState({ coords: null, status: 'unavailable', isSettled: true });
      return;
    }

    let cancelled = false;
    let settleTimer: number | undefined;

    if (!lastCoords) {
      setState((s) => ({ ...s, status: 'prompting' }));

      const settle = () => {
        if (!cancelled) setState((s) => ({ ...s, isSettled: true }));
      };

      settleTimer = window.setTimeout(settle, DEFAULT_SETTLE_MS);

      void isPermissionGranted().then((granted) => {
        if (cancelled || !granted) return;

        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settle, GRANTED_SETTLE_MS);
      });
    }

    requestPosition().then(
      (coords) => {
        if (!cancelled) setState({ coords, status: 'granted', isSettled: true });
      },
      (err: GeolocationPositionError) => {
        if (cancelled) return;

        if (err.code === err.PERMISSION_DENIED) {
          setState({ coords: null, status: 'denied', isSettled: true });
          return;
        }

        // Не успели уточнить — остаёмся на известной точке
        setState((s) =>
          s.coords
            ? { ...s, isSettled: true }
            : { coords: null, status: 'unavailable', isSettled: true }
        );
      }
    );

    return () => {
      cancelled = true;
      window.clearTimeout(settleTimer);
    };
  }, [enabled]);

  return state;
}
